import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { TKey } from '~/lib/i18n-locales';
import { cn } from '~/utils/cn';

interface AboutSectionHeaderProps {
  title?: TKey;
  text?: TKey;
  className?: string;
}

interface AboutSectionProps extends Omit<AboutSectionHeaderProps, 'className'> {
  /** Sets the section's id to `spy-<sectionId>`, which the scroll spy observes. */
  sectionId?: string;
  children?: ReactNode;
  alternate?: boolean;
}

function AboutSectionHeader({ title, text, className }: AboutSectionHeaderProps) {
  const { t } = useTranslation();

  if (!title && !text) {
    return null;
  }

  return (
    <div className={cn('mx-auto flex max-w-3xl flex-col gap-4', className)}>
      {title && <h2 className="font-heading font-semibold text-2xl leading-[1.1] sm:text-center sm:text-3xl md:text-4xl">{t(title)}</h2>}
      {text && (
        <p className="text-muted-foreground sm:text-center sm:text-lg sm:leading-7">
          <Trans i18nKey={text as never} />
        </p>
      )}
    </div>
  );
}

export function AboutSection({ title, text, sectionId, children, alternate }: AboutSectionProps) {
  return (
    <section
      id={sectionId ? `spy-${sectionId}` : undefined}
      className={cn('overflow-hidden px-4 py-8 md:py-12 lg:py-20', alternate && 'bg-accent/40 dark:bg-transparent')}
    >
      <AboutSectionHeader title={title} text={text} className="mb-12" />
      {children}
    </section>
  );
}
